import type { Block } from "@/types/proposals";

interface ShapeBlockProps {
  block: Block;
  onUpdate: (updates: Partial<Block>) => void;
  onSelect: () => void;
}

export function ShapeBlock({ block, onUpdate, onSelect }: ShapeBlockProps) {
  const content = block.content || {};
  const shapeType = content.shapeType || 'rectangle';
  const fillColor = content.fillColor || '#3B82F6';
  const strokeColor = content.strokeColor || '#1E40AF';
  const strokeWidth = content.strokeWidth || 2;
  const rotation = content.rotation || 0;
  const blockSize = block.settings?.blockSize || 'medium';
  const textAlign = block.style?.textAlign || 'left';

  const heightValue = {
    small: 40,
    medium: 80,
    large: 160,
  }[blockSize];

  const containerAlignClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  }[textAlign];

  const renderShape = () => {
    switch (shapeType) {
      case 'rectangle':
        return (
          <div
            style={{
              width: '100%',
              height: `${heightValue}px`,
              backgroundColor: fillColor,
              border: `${strokeWidth}px solid ${strokeColor}`,
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.2s',
            }}
          />
        );
      case 'circle':
        return (
          <div
            style={{
              width: `${heightValue}px`,
              height: `${heightValue}px`,
              backgroundColor: fillColor,
              border: `${strokeWidth}px solid ${strokeColor}`,
              borderRadius: '50%',
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.2s',
            }}
          />
        );
      case 'line':
        return (
          <div
            style={{
              width: '100%',
              height: `${strokeWidth}px`,
              backgroundColor: strokeColor,
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.2s',
            }}
          />
        );
      case 'triangle':
        return (
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: `${heightValue / 2}px solid transparent`,
              borderRight: `${heightValue / 2}px solid transparent`,
              borderBottom: `${heightValue}px solid ${fillColor}`,
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.2s',
            }}
          />
        );
      case 'arrow':
        return (
          <svg width="100%" height={`${heightValue}px`} viewBox="0 0 100 100" style={{ transform: `rotate(${rotation}deg)` }}>
            <defs>
              <marker
                id={`arrowhead-${block.id}`}
                markerWidth="10"
                markerHeight="10"
                refX="9"
                refY="3"
                orient="auto"
              >
                <polygon points="0 0, 10 3, 0 6" fill={strokeColor} />
              </marker>
            </defs>
            <line
              x1="10"
              y1="50"
              x2="90"
              y2="50"
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              markerEnd={`url(#arrowhead-${block.id})`}
            />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className={`w-full flex items-center ${containerAlignClass}`} 
      onClick={onSelect}
      style={{ backgroundColor: block.style?.backgroundColor || 'transparent' }}
    >
      {renderShape()}
    </div>
  );
}
